import React, { FC, useCallback, useState } from 'react';

import { Level, StandardRuleCompliance } from '@ariksa/compliance-policies/api';
import { Box, UseDisclosureProps, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import {
  ChakraMarkdown,
  CustomTooltip,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { BlueprintSubControlsDrawer } from 'containers/PolicyHub/Blueprints/Components/Blueprint/BlueprintPolicies/ComplianceBlueprint/BlueprintSubControlsDrawer';

import { selectBlueprints } from '../../../../selectors';
import { actions } from '../../../../slice';

interface Props {
  searchTerm?: string;
  addPolicy?: UseDisclosureProps;
  selectedPolicies?: string[];
  setSelectedPolicies?(value: string[]);
  action?: 'Create' | 'Show' | 'Update';
}

export const ComplianceBlueprintControls: FC<Props> = props => {
  const {
    searchTerm,
    addPolicy,
    selectedPolicies,
    setSelectedPolicies,
    action = 'Show',
  } = props;
  const dispatch = useDispatch();
  const { complianceControls, blueprint } = useSelector(selectBlueprints);
  const subControlsDrawer = useDisclosure();
  const [
    selectedControl,
    setSelectedControl,
  ] = useState<StandardRuleCompliance>({} as StandardRuleCompliance);

  //get policies
  const getComplianceControls = useCallback(() => {
    dispatch(
      actions.getComplianceControls({
        q: { blueprintId: blueprint.data?.id, level: Level.Control },
      }),
    );
  }, [dispatch, blueprint.data]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Control</Box>,
      accessor: 'control',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={blueprint.data.compliance!}
          iconFilled={false}
          iconSize="regular"
        >
          {value}
        </WithResourceIcon>
      ),
    },
    {
      header: 'Description',
      accessor: 'control_description',
      align: 'left',
      render: ({ value }) => (
        <CustomTooltip
          label={<ChakraMarkdown content={value || '-'} />}
          styles={{ text: { w: 'full' } }}
        >
          <Box whiteSpace="wrap" noOfLines={2} flex={1}>
            {value || '-'}
          </Box>
        </CustomTooltip>
      ),
    },
    {
      header: '# of Policies',
      accessor: 'rules',
      render: ({ value }) => value.total ?? '-',
      align: 'left',
    },
    {
      header: 'Entities',
      accessor: 'check_stats.entities_with_issues',
      align: 'left',
    },
    {
      header: 'Policy Exceptions',
      accessor: 'rules',
      render: ({ value }) => {
        if (value?.total && value?.selected) {
          return value?.total - value?.selected;
        } else {
          return '-';
        }
      },
      align: 'left',
    },
    /* {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            label="Exclude control"
            isDisabled={
              !blueprint.data.created_by ||
              blueprint.data.created_by === 'system'
            }
            icon={<PolicyExceptionIcon />}
          />
        </Center>
      ),
    },*/
  ];

  useDeepCompareEffect(() => {
    getComplianceControls();
  }, [getComplianceControls]);

  return (
    <Box h="full" flex={1}>
      <Table
        data={complianceControls.data}
        columns={columns}
        isLoading={complianceControls.isLoading}
        cursor="pointer"
        styles={{ header: { zIndex: 800 } }}
        onRowClick={row => {
          setSelectedControl(row);
          subControlsDrawer.onOpen();
        }}
        pagination={{
          totalCount: complianceControls.page.totalCount,
          pageInfo: complianceControls.page.info,
          onChange: pageInfo =>
            dispatch(actions.updateComplianceControlsPagination(pageInfo)),
        }}
      />
      {subControlsDrawer.isOpen && (
        <BlueprintSubControlsDrawer
          control={selectedControl}
          isOpen={subControlsDrawer.isOpen}
          onClose={subControlsDrawer.onClose}
          addPolicy={addPolicy}
          selectedPolicies={selectedPolicies}
          setSelectedPolicies={setSelectedPolicies}
          action={action}
        />
      )}
    </Box>
  );
};
